import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form } from 'react-bootstrap';
import AdminSidebar from '../components/AdminSidebar';
import AdminNavbar from '../components/AdminNavbar';
import { useSidebar } from '../components/SidebarContext';
import QuestionModal from './QuestionModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import * as XLSX from 'xlsx';

// Function to detect LaTeX patterns
const hasLatex = (text) => {
  if (!text || typeof text !== 'string') {
    console.log(`No LaTeX: Text is ${text}`);
    return false;
  }
  const latexPatterns = [
    /\\[\w]+/,
    /\^{?[\w\d\s]+}?/,
    /_{?[\w\d\s]+}?/,
    /\{.*?\}/,
    /[\$]/,
    /\\[a-zA-Z]+/,
  ];
  const result = latexPatterns.some((pattern) => pattern.test(text));
  console.log(`Checking LaTeX in: "${text}" -> ${result}`);
  return result;
};

const cleanText = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(/^\uFEFF/, '')
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u200B\u200C\u200D\uFEFF]/g, '')
    .replace(/\u00A0/g, ' ')
    .replace(/[\u2000-\u200F\u2028-\u202F]/g, ' ')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\s+/g, ' ')
    .trim();
};

// Function to render text with LaTeX if present
const renderTextWithLatex = (text, isBlock = false) => {
  if (!text || typeof text !== 'string') return text || 'Not available';
  if (hasLatex(text)) {
    try {
      const cleanedText = text.replace(/\$/g, '');
      return isBlock ? <BlockMath math={cleanedText} /> : <InlineMath math={cleanedText} />;
    } catch (error) {
      console.error(`Error rendering LaTeX for text "${text}":`, error);
      return text;
    }
  }
  return text;
};

const BulkUploadQuestions = () => {
  const { isSidebarOpen = true } = useSidebar() || {};
  const sidebarWidth = isSidebarOpen ? '250px' : '70px';

  const [formData, setFormData] = useState({ section: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('No file chosen');
  const [parsedData, setParsedData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [sections, setSections] = useState([]);
  const [isSectionDropdownOpen, setIsSectionDropdownOpen] = useState(false);
  const [isAddSectionModalOpen, setIsAddSectionModalOpen] = useState(false);
  const [newSection, setNewSection] = useState('');
  const [sectionSearch, setSectionSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: '', modalOnly: false });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    fetchSections();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const showNotification = (message, type = 'error', modalOnly = false) => {
    setNotification({ message, type, modalOnly });
    setTimeout(() => {
      setNotification({ message: '', type: '', modalOnly: false });
    }, 5000);
  };

  const handleCloseNotification = () => {
    setNotification({ message: '', type: '', modalOnly: false });
  };

  const fetchSections = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/sections`);
      if (!response.ok) throw new Error('Failed to fetch sections');
      const text = await response.text();
      const data = JSON.parse(text);
      setSections(data);
    } catch (error) {
      console.error('Error fetching sections:', error);
      showNotification(`Unable to load sections: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prev) => ({ ...prev, section: '' }));
  };

  const handleSectionSelect = (section) => {
    setFormData({ ...formData, section: section.name });
    setSectionSearch(section.name);
    setErrors((prev) => ({ ...prev, section: '' }));
    setIsSectionDropdownOpen(false);
  };

  const handleAddSection = async () => {
    if (!newSection.trim()) {
      showNotification('Please enter a Section', 'error', true);
      return;
    }

    const existingSection = sections.find(
      (section) => section.name.toLowerCase() === newSection.toLowerCase()
    );
    if (existingSection) {
      showNotification('Section already exists!', 'error', true);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/sections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newSection }),
      });

      if (!response.ok) {
        const text = await response.text();
        const error = JSON.parse(text);
        throw new Error(error.error || 'Failed to create section');
      }

      const newSectionData = await response.json();
      setSections([...sections, newSectionData]);
      setFormData({ ...formData, section: newSectionData.name });
      setSectionSearch(newSectionData.name);
      setErrors((prev) => ({ ...prev, section: '', newSection: '' }));
      setNewSection('');
      setIsAddSectionModalOpen(false);
      showNotification('Section added successfully!', 'success');
    } catch (error) {
      console.error('Error adding section:', error);
      showNotification(`Failed to add section: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (['xlsx', 'xls'].includes(fileExtension)) {
      setSelectedFile(file);
      setFileName(file.name);
      setErrors((prev) => ({ ...prev, file: '' }));
      parseFile(file);
    } else {
      setSelectedFile(null);
      setFileName('No file chosen');
      setParsedData([]);
      showNotification('Please upload a valid Excel file (.xlsx or .xls)', 'error');
    }
  };

  const parseFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target.result;
        const workbook = XLSX.read(arrayBuffer, { type: 'array', raw: false });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false });

        if (data.length === 0) {
          showNotification('Excel file is empty', 'error');
          setParsedData([]);
          return;
        }

        const headers = data[0].map((header) => (header ? header.trim().toLowerCase() : ''));
        const expectedHeaders = ['questiontext', 'options', 'correctanswer', 'difficulty'];

        const headersMatch = expectedHeaders.every(
          (header, index) => headers[index] && headers[index] === header
        );

        if (!headersMatch) {
          showNotification(
            'Invalid headers. Expected: questionText,options,correctAnswer,difficulty',
            'error'
          );
          setParsedData([]);
          setSelectedFile(null);
          setFileName('No file chosen');
          document.getElementById('excelFile').value = '';
          return;
        }

        const processedData = data.slice(1).filter(row => row.some(cell => cell && cell.trim() !== '')).map((row) => ({
          questionText: cleanText(row[0] || ''),
          options: cleanText(row[1] || ''),
          correctAnswer: cleanText(row[2] || ''),
          difficulty: cleanText(row[3] || ''),
        }));

        if (processedData.length === 0) {
          showNotification('No valid data rows found in Excel', 'error');
          setParsedData([]);
          return;
        }

        setParsedData(processedData);
      } catch (error) {
        console.error('Error parsing file:', error);
        showNotification('Error parsing file. Please check the format.', 'error');
        setParsedData([]);
        setSelectedFile(null);
        setFileName('No file chosen');
        document.getElementById('excelFile').value = '';
      }
    };
    reader.onerror = () => {
      showNotification('Error reading Excel file', 'error');
      setParsedData([]);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileClear = () => {
    setSelectedFile(null);
    setFileName('No file chosen');
    setParsedData([]);
    setErrors((prev) => ({ ...prev, file: '' }));
    document.getElementById('excelFile').value = '';
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.section) {
      newErrors.section = 'Section is required';
      showNotification('Please select a section', 'error');
    }
    if (!selectedFile) {
      newErrors.file = 'Excel file is required';
      showNotification('Please upload an Excel file', 'error');
    }
    if (parsedData.length === 0) {
      newErrors.file = 'No valid data found in the Excel file';
      showNotification('No valid data found in the Excel file', 'error');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const formDataObj = new FormData();
    formDataObj.append('excelFile', selectedFile);
    formDataObj.append('section', formData.section);

    setIsUploading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/questions/bulk-upload`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { successCount, errorCount } = response.data;

      if (successCount > 0 && errorCount === 0) {
        showNotification(
          `${successCount} question${successCount > 1 ? 's' : ''} uploaded successfully.`,
          'success'
        );
      } else if (successCount > 0 && errorCount > 0) {
        showNotification(
          `${successCount} question${successCount > 1 ? 's' : ''} uploaded successfully, but ${errorCount} failed due to invalid data`,
          'error'
        );
      } else {
        showNotification('Upload failed: No valid questions found in Excel', 'error');
      }
      setErrors({});
    } catch (error) {
      let errorMessage = 'Upload failed due to invalid data';
      if (error.response) {
        const { error: serverError, details } = error.response.data;
        errorMessage = serverError || details || error.message;
      } else {
        errorMessage = `Upload failed: ${error.message}`;
      }
      console.error('Upload error details:', error.response?.data || error.message);
      showNotification(errorMessage, 'error');
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      setFileName('No file chosen');
      setParsedData([]);
      document.getElementById('excelFile').value = '';
    }
  };

  const handleSaveQuestion = async (data) => {
    try {
      const validOptions = data.options.filter((opt) => opt.trim()).length;
      if (validOptions < 2 || validOptions > 6) {
        showNotification('Question must have 2–6 non-empty options', 'error');
        return;
      }

      const questionData = {
        questionText: data.questionText,
        options: data.options,
        correctAnswer: data.correctAnswer,
        section: data.section,
        difficulty: data.difficulty,
      };
      await axios.post(`${API_BASE_URL}/questions`, questionData);
      setShowQuestionModal(false);
      showNotification('Question added successfully!', 'success');
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      const validationErrors = error.response?.data?.validationErrors || [];
      showNotification(
        `Failed to add question: ${errorMessage}${validationErrors.length ? '; Details: ' + validationErrors.join(', ') : ''}`,
        'error'
      );
    }
  };

  const handleDownloadTemplate = () => {
    const headers = ['questionText', 'options', 'correctAnswer', 'difficulty'];
    const sampleData = [
      ['What is the value of \\frac{1}{2} + \\frac{1}{3}?', '5/6;2/5;1/3;3/4', "'5/6'", 'medium'],
      ['What is the integral ∫x² dx from 0 to 1?', '1/3;1/2;1;0', "'1/3'", 'hard'],
      ['Find d/dx[sin(x)]', 'cos(x);-cos(x);tan(x);-sin(x)', "'cos(x)'", 'medium'],
      ['Find the sum ∑_{i=1}^{n} i', 'n(n+1)/2;n²;n;n/2', "'n(n+1)/2'", 'hard'],
      ['θ ∈ ∀ ∃ ∅ ∞ ≠ ≤ ≥ ∑ √ ∫ ∂ ∇ → ← ↔ ⇒', 'n(n+1)/2;n²;n;n/2;⇔ ∧ ∨ ¬', "'n(n+1)/2'", 'hard'],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...sampleData]);

    // Set cell format to text for correctAnswer column
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      const cellAddress = `C${row + 1}`; // C: correctAnswer
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].t = 's'; // Set type to string
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Questions');
    XLSX.writeFile(workbook, 'stem-symbols-template.xlsx');
  };

  const handleOpenModal = () => {
    setShowQuestionModal(true);
    setFormData({ ...formData, section: '' });
  };

  const handleCloseModal = () => {
    setShowQuestionModal(false);
  };

  const filteredSections = sections.filter((section) =>
    section.name.toLowerCase().includes(sectionSearch.toLowerCase())
  );

  const styles = {
    container: {
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      minHeight: '100vh',
      background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
      fontFamily: "'Roboto', sans-serif",
    },
    sidebar: {
      width: sidebarWidth,
      borderRadius: '30px',
      height: '100vh',
      flexShrink: 0,
      fontFamily: "'Roboto', sans-serif",
    },
    content: {
      flex: 1,
      padding: '15px',
      margin: '6rem 2rem 2rem 2rem',
      backgroundColor: 'white',
      overflowY: 'auto',
      transition: 'margin-left 0.3s ease',
      minHeight: 'calc(100vh - 8rem)',
      maxHeight: 'calc(100vh - 8rem)',
      borderTopLeftRadius: '1rem',
      borderTopRightRadius: '1rem',
      borderBottomLeftRadius: '1rem',
      borderBottomRightRadius: '1rem',
      maxWidth: `calc(100vw - ${sidebarWidth} - 4rem)`,
      width: '100%',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "'Roboto', sans-serif",
      position: 'relative',
    },
    scrollableContent: {
      flex: 1,
      width: '100%',
      height: '100%',
      overflowY: 'auto',
      paddingBottom: '1rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
      position: 'relative',
      fontFamily: "'Roboto', sans-serif",
      maxHeight: 'calc(90vh - 100px)',
    },
    cardContainer: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
      width: '100%',
      maxWidth: '500px',
      margin: '0 auto',
      fontFamily: "'Roboto', sans-serif",
    },
    notificationContainer: {
      position: 'absolute',
      top: '86px',
      right: '15px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      maxWidth: '250px',
      zIndex: 100,
      fontFamily: "'Roboto', sans-serif",
    },
    notification: {
      fontSize: '0.85rem',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
      fontWeight: '400',
      backgroundColor: '#F3E8FF',
      padding: '5px 10px',
      borderRadius: '4px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      animation: 'slideInFromRight 0.5s ease-out',
      wordBreak: 'break-word',
      fontFamily: "'Roboto', sans-serif",
    },
    notificationClose: {
      cursor: 'pointer',
      fontSize: '0.75rem',
      alignItems: 'center',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
    },
    modalNotificationContainer: {
      position: 'absolute',
      top: '15px',
      right: '15px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      maxWidth: '250px',
      zIndex: 100,
      fontFamily: "'Roboto', sans-serif",
    },
    uploadContainerStyle: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      border: '2px dashed #C5A8FF',
      borderRadius: '0.375rem',
      padding: '2rem',
      backgroundColor: '#F3F4F6',
      width: '100%',
      maxWidth: '500px',
      height: '180px',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease',
      margin: '8px auto',
      fontFamily: "'Roboto', sans-serif",
    },
    uploadTextStyle: {
      fontSize: '1rem',
      fontWeight: '500',
      color: '#6B21A8',
      textAlign: 'center',
      marginBottom: '0.5rem',
      fontFamily: "'Roboto', sans-serif",
    },
    uploadSubTextStyle: {
      fontSize: '0.875rem',
      color: '#808080',
      textAlign: 'center',
      fontFamily: "'Roboto', sans-serif",
    },
    filePreviewStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: '#F3F4F6',
      border: '1px solid #D1D5DB',
      borderRadius: '0.375rem',
      padding: '0.5rem 1rem',
      width: '100%',
      maxWidth: '500px',
      boxSizing: 'border-box',
      margin: '8px auto',
      fontFamily: "'Roboto', sans-serif",
    },
    fileInfoStyle: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
      flex: 1,
      fontFamily: "'Roboto', sans-serif",
    },
    fileNameTextStyle: {
      fontSize: '0.875rem',
      color: '#6B21A8',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontFamily: "'Roboto', sans-serif",
    },
    progressBarContainerStyle: {
      width: '100%',
      height: '5px',
      backgroundColor: '#E5E7EB',
      borderRadius: '5px',
      overflow: 'hidden',
      fontFamily: "'Roboto', sans-serif",
    },
    progressBarStyle: {
      width: '100%',
      height: '100%',
      backgroundColor: '#6B21A8',
      transition: 'width 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    fileActionsStyle: {
      display: 'flex',
      gap: '0.5rem',
      fontFamily: "'Roboto', sans-serif",
    },
    actionButtonStyle: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '1rem',
      padding: '0.25rem',
      fontFamily: "'Roboto', sans-serif",
    },
    uploadButtonContainerStyle: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '8px',
      marginBottom: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    buttonStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.5rem 1.2rem',
      fontSize: '0.875rem',
      fontWeight: '600',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '0.05rem',
      cursor: 'pointer',
      borderRadius: '1.5rem',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
      whiteSpace: 'nowrap',
      fontFamily: "'Roboto', sans-serif",
    },
    buttonDisabledStyle: {
      opacity: 0.6,
      cursor: 'not-allowed',
      fontFamily: "'Roboto', sans-serif",
    },
    dropdownContainerStyle: {
      position: 'relative',
      width: '100%',
      maxWidth: '350px',
      margin: '0 auto',
      fontFamily: "'Roboto', sans-serif",
    },
    dropdownWrapperStyle: {
      display: 'flex',
      alignItems: 'center',
      gap: '0',
      fontFamily: "'Roboto', sans-serif",
    },
    dropdownButtonStyle: {
      padding: '10px',
      borderRadius: '5px 0 0 5px',
      border: '1px solid #ccc',
      borderRight: 'none',
      width: '100%',
      backgroundColor: '#fff',
      textAlign: 'left',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '0.875rem',
      color: '#374151',
      height: '38px',
      fontFamily: "'Roboto', sans-serif",
    },
    dropdownMenuStyle: {
      position: 'absolute',
      top: '100%',
      left: 0,
      width: 'calc(100% - 32px)',
      maxHeight: '150px',
      overflowY: 'auto',
      border: '1px solid #ccc',
      borderRadius: '5px',
      backgroundColor: '#fff',
      zIndex: 10,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      padding: '0.5rem',
      fontFamily: "'Roboto', sans-serif",
    },
    dropdownItemStyle: {
      padding: '0.5rem',
      cursor: 'pointer',
      borderRadius: '0.375rem',
      transition: 'background-color 0.3s ease',
      fontSize: '0.875rem',
      color: '#374151',
      fontFamily: "'Roboto', sans-serif",
    },
    searchInputContainerStyle: {
      position: 'relative',
      width: '100%',
      fontFamily: "'Roboto', sans-serif",
    },
    searchInputStyle: {
      padding: '10px',
      borderRadius: '5px 0 0 5px',
      border: '1px solid #ccc',
      borderRight: 'none',
      width: '100%',
      fontSize: '0.875rem',
      boxSizing: 'border-box',
      fontFamily: "'Roboto', sans-serif",
    },
    labelStyle: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.3rem',
      display: 'block',
      textAlign: 'left',
      fontFamily: "'Roboto', sans-serif",
    },
    plusIconStyle: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '45px',
      height: '44px',
      borderRadius: '0 5px 5px 0',
      border: '1px solid #ccc',
      borderLeft: 'none',
      backgroundColor: '#C5A8FF',
      color: '#fff',
      fontSize: '1.5rem',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease',
      lineHeight: '2',
      textAlign: 'center',
      fontFamily: "'Roboto', sans-serif",
    },
    modalOverlayStyle: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px',
      fontFamily: "'Roboto', sans-serif",
    },
    modalStyle: {
      backgroundColor: '#ffffff',
      borderRadius: '0.75rem',
      boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
      padding: '1rem',
      width: '100%',
      maxWidth: '350px',
      boxSizing: 'border-box',
      position: 'relative',
      fontFamily: "'Roboto', sans-serif",
    },
    modalHeaderStyle: {
      fontSize: '0.875rem',
      fontWeight: '600',
      marginBottom: '0.5rem',
      color: '#6B21A8',
      textAlign: 'left',
      fontFamily: "'Roboto', sans-serif",
    },
    formGridStyle: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '0.75rem',
      marginBottom: '0.5rem',
      fontFamily: "'Roboto', sans-serif",
    },
    inputStyle: {
      width: '100%',
      padding: '0.5rem',
      border: '1px solid #D1D5DB',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      transition: 'border-color 0.3s ease',
      boxSizing: 'border-box',
      fontFamily: "'Roboto', sans-serif",
    },
    modalButtonContainerStyle: {
      display: 'flex',
      justifyContent: 'right',
      gap: '0.5rem',
      marginTop: '0.75rem',
      fontFamily: "'Roboto', sans-serif",
    },
    modalCancelButtonStyle: {
      backgroundColor: '#D1D5DB',
      color: '#374151',
      paddingLeft: '15px',
      paddingRight: '15px',
      paddingTop: '5px',
      paddingBottom: '5px',
      border: 'none',
      borderRadius: '5rem',
      fontSize: '14px',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    modalAddButtonStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '15px 30px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '0.05rem',
      cursor: 'pointer',
      borderRadius: '5rem',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
      width: '2px',
      height: '1px',
      fontFamily: "'Roboto', sans-serif",
    },
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          html, body {
            margin: 0;
            padding: 0;
            height: '100vh',
            width: '100vw',
            overflow-x: hidden;
          }
          @keyframes slideInFromRight {
            0% { 
              transform: translateX(100%);
              opacity: 0;
            }
            100% {
              transform: translateX(0);
              opacity: 1;
            }
          }
          .glow-button:hover:not(:disabled) {
            background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF);
            transform: scale(1.05);
            animation: glow 1.5s ease-in-out infinite;
          }
          .modal-cancel-button:hover:not(:disabled) {
            background-color: #B0BEC5;
          }
          .input-field:focus {
            border-color: #6B21A8;
            outline: none;
            box-shadow: 0 0 5px rgba(107, 33, 168, 0.5);
          }
          .upload-container:hover {
            background-color: #E5E7EB;
          }
          .delete-button:hover {
            color: #808080;
          }
          .checkmark-button:hover {
            color: #32CD32;
          }
          .dropdown-item:hover {
            background-color: #F3F4F6;
          }
          .plus-icon:hover {
            background-color: #A78BFA;
          }
          .content::-webkit-scrollbar {
            width: 8px;
          }
          .content::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          .content::-webkit-scrollbar-thumb {
            background: rgb(254, 253, 254);
            border-radius: 4px;
          }
          .content::-webkit-scrollbar-thumb:hover {
            background: rgb(254, 254, 254);
          }
          .scrollable-content::-webkit-scrollbar {
            width: 8px;
          }
          .scrollable-content::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          .scrollable-content::-webkit-scrollbar-thumb {
            background: rgb(253, 252, 253);
            border-radius: 4px;
          }
          .scrollable-content::-webkit-scrollbar-thumb:hover {
            background: rgb(253, 252, 253);
          }
          .katex {
            font-size: 1em !important;
            display: inline-flex !important;
            vertical-align: middle;
          }
          .katex-display {
            margin: 0 !important;
            display: block !important;
          }
        `}
      </style>

      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100%',
          width: sidebarWidth,
          zIndex: 10,
          transition: 'width 0.3s ease',
          ...styles.sidebar,
        }}
      >
        <AdminSidebar />
      </div>

      <div style={{ marginLeft: sidebarWidth, width: `calc(100% - ${sidebarWidth})`, transition: 'margin-left 0.3s ease' }}>
        <AdminNavbar />
        <div style={styles.content} className="content">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.5rem',
              marginTop: '18px',
              marginBottom: '8px',
              width: 'calc(100% - 30px)',
              marginLeft: '15px',
              marginRight: '15px',
            }}
          >
            <button style={styles.buttonStyle} onClick={handleOpenModal} className="glow-button">
              Add Question
            </button>
            <button style={styles.buttonStyle} onClick={handleDownloadTemplate} className="glow-button">
              Download Excel Template
            </button>
          </div>

          <div style={styles.notificationContainer}>
            {notification.message && !notification.modalOnly && (
              <div style={styles.notification}>
                {notification.message}
                <FontAwesomeIcon
                  icon={faTimes}
                  style={styles.notificationClose}
                  onClick={handleCloseNotification}
                  aria-label="Close message"
                />
              </div>
            )}
          </div>

          <div style={styles.scrollableContent} className="scrollable-content">
            <div style={styles.cardContainer}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '100%', marginTop: '2rem' }}>
                <div style={styles.dropdownContainerStyle}>
                  <div style={styles.dropdownWrapperStyle}>
                    <div style={styles.searchInputContainerStyle}>
                      <input
                        type="text"
                        value={sectionSearch}
                        onChange={(e) => setSectionSearch(e.target.value)}
                        placeholder={formData.section || 'Search sections...'}
                        style={styles.searchInputStyle}
                        className="input-field"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsSectionDropdownOpen(true);
                        }}
                        onFocus={() => setIsSectionDropdownOpen(true)}
                      />
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#374151', fontSize: '0.875rem', fontFamily: "'Roboto', sans-serif" }}
                        onClick={() => setIsSectionDropdownOpen(!isSectionDropdownOpen)}
                      />
                    </div>
                    <span
                      style={styles.plusIconStyle}
                      className="plus-icon"
                      onClick={() => setIsAddSectionModalOpen(true)}
                      aria-label="Add new section"
                    >
                      +
                    </span>
                  </div>
                  {isSectionDropdownOpen && (
                    <div style={styles.dropdownMenuStyle}>
                      {filteredSections.length === 0 ? (
                        <div style={{ padding: '0.5rem', color: '#888', fontFamily: "'Roboto', sans-serif" }}>No sections found</div>
                      ) : (
                        filteredSections.map((section) => (
                          <div
                            key={section._id}
                            style={styles.dropdownItemStyle}
                            className="dropdown-item"
                            onClick={() => handleSectionSelect(section)}
                          >
                            {section.name}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  <Form.Control.Feedback type="invalid" style={{ display: errors.section ? 'block' : 'none', textAlign: 'left', fontSize: '0.75rem', fontFamily: "'Roboto', sans-serif" }}>
                    {errors.section}
                  </Form.Control.Feedback>
                </div>

                <div
                  style={styles.uploadContainerStyle}
                  className="upload-container"
                  onClick={() => document.getElementById('excelFile').click()}
                >
                  <div style={styles.uploadTextStyle}>Click here to upload an Excel file</div>
                  <div style={styles.uploadSubTextStyle}>Only .xlsx or .xls files allowed.</div>
                  <div style={styles.uploadSubTextStyle}>Options must be semicolon-separated (;).</div>
                  <div style={styles.uploadSubTextStyle}>Each question must have a min of 2 and a max of 6 options.</div>
                  <div style={styles.uploadSubTextStyle}>Correct answer must be enclosed within single quotation marks ('__').</div>
                    <input
                    id="excelFile"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    disabled={isUploading}
                    style={{ display: 'none' }}
                  />
                </div>

                {selectedFile && (
                  <div style={styles.filePreviewStyle}>
                    <div style={styles.fileInfoStyle}>
                      <div style={styles.fileNameTextStyle}>
                        {fileName} | Rows: {parsedData.length}
                      </div>
                      <div style={styles.progressBarContainerStyle}>
                        <div style={styles.progressBarStyle}></div>
                      </div>
                    </div>
                    <div style={styles.fileActionsStyle}>
                      <button
                        style={{ ...styles.actionButtonStyle, color: '#32CD32' }}
                        className="checkmark-button"
                        aria-label="File uploaded"
                      >
                        ✔
                      </button>
                      <button
                        style={{ ...styles.actionButtonStyle, color: '#808080' }}
                        className="delete-button"
                        onClick={handleFileClear}
                        aria-label="Delete file"
                      >
                        ✗
                      </button>
                    </div>
                  </div>
                )}

                <Form.Control.Feedback type="invalid" style={{ display: errors.file ? 'block' : 'none', textAlign: 'left', fontSize: '0.75rem', fontFamily: "'Roboto', sans-serif" }}>
                  {errors.file}
                </Form.Control.Feedback>

                <div style={styles.uploadButtonContainerStyle}>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isUploading || !selectedFile || parsedData.length === 0}
                    style={{
                      ...styles.buttonStyle,
                      ...(isUploading || !selectedFile || parsedData.length === 0 ? styles.buttonDisabledStyle : {}),
                    }}
                    className="glow-button"
                  >
                    {isUploading ? 'Uploading...' : `Upload ${parsedData.length} Questions`}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {isAddSectionModalOpen && (
            <div style={styles.modalOverlayStyle}>
              <div style={styles.modalStyle}>
                <div style={styles.modalNotificationContainer}>
                  {notification.message && (
                    <div style={styles.notification}>
                      {notification.message}
                      <FontAwesomeIcon
                        icon={faTimes}
                        style={styles.notificationClose}
                        onClick={handleCloseNotification}
                      />
                    </div>
                  )}
                </div>
                <div style={styles.modalHeaderStyle}>Add New Section</div>
                <input
                  type="text"
                  value={newSection}
                  onChange={(e) => setNewSection(e.target.value)}
                  style={styles.inputStyle}
                  className="input-field"
                  placeholder="Enter section name"
                />
                <div style={styles.modalButtonContainerStyle}>
                  <button
                    style={styles.modalCancelButtonStyle}
                    onClick={() => {
                      setNewSection('');
                      setIsAddSectionModalOpen(false);
                      setFormData({ ...formData, section: '' });
                    }}
                    className="modal-cancel-button"
                  >
                    Cancel
                  </button>
                  <button
                    style={styles.modalAddButtonStyle}
                    onClick={handleAddSection}
                    className="glow-button"
                    disabled={loading}
                  >
                    {loading ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showQuestionModal && (
            <div style={styles.modalOverlayStyle}>
              <div style={styles.modalStyle}>
                <div style={styles.modalNotificationContainer}>
                  {notification.message && (
                    <div style={styles.notification}>
                      {notification.message}
                      <FontAwesomeIcon
                        icon={faTimes}
                        style={styles.notificationClose}
                        onClick={handleCloseNotification}
                      />
                    </div>
                  )}
                </div>
                <div style={styles.modalHeaderStyle}>Add New Question</div>
                <QuestionModal
                  show={showQuestionModal}
                  handleClose={handleCloseModal}
                  onSave={handleSaveQuestion}
                  modalStyles={{
                    formGrid: styles.formGridStyle,
                    input: styles.inputStyle,
                    label: styles.labelStyle,
                    modalButtonContainer: styles.modalButtonContainerStyle,
                    modalCancelButton: styles.modalCancelButtonStyle,
                    button: styles.modalAddButtonStyle,
                    buttonDisabled: styles.buttonDisabledStyle,
                  }}
                  isMobile={isMobile}
                  sections={sections}
                  renderTextWithLatex={renderTextWithLatex}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadQuestions;